import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import isBetween from 'dayjs/plugin/isBetween'
import { AddToCalendarButton } from 'add-to-calendar-button-react'
import pLimit from 'p-limit'

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import VideoIcon from '@mui/icons-material/Videocam'
import BackArrowIcon from '@mui/icons-material/ArrowBack'
import VerifiedIcon from '@mui/icons-material/Verified'

import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios, { pluckError } from 'src/utilities/http-client'
import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import Loading from 'src/v3/components/loading'
import {
  accreditationDict,
  AccreditationType,
} from 'src/v3/events/accreditations'
import {
  selectRoles,
  selectUseEvents,
  selectUserID,
} from 'src/v3/settings/settings.slice'
import { DataGrid } from '@mui/x-data-grid'
import {
  fetchEvents,
  fetchAccreditations,
  fetchEventAccreditations,
  selectAccreditationsStatus,
  selectEventAccreditationsStatus,
  selectEventById,
  selectEventsStatus,
  selectAccreditationsByTypeByEvent,
  selectSessionsByEventID,
  selectSessionsStatus,
  fetchSessions,
  selectCompletionEventsByID,
  selectCompletionEventsStatusByID,
  fetchEventCompletionEvents,
  selectUsersByEvent,
  selectUsersStatusByEvent,
  fetchEventCompletionEventsBySession,
  selectQuizByEvent,
  selectQuizStatusByEvent,
  fetchQuiz,
  fetchUsersByEvent,
  fetchQuizSessionResult,
  selectQuizSessionResultsStatus,
  selectQuizSessionResults,
  selectEventCERecordsStatus,
  selectEventCERecords,
  fetchEventCERecords,
  selectInstructorsStatus,
  fetchInstructors,
  selectInstructors,
  selectSyllabusStatusByEventID,
  selectSyllabusByEventID,
  fetchSyllabus,
  selectSessionCapacity,
  HydratedSession,
} from './events.slice'
import {
  CalendarIcon,
  ClockIcon,
  CloseIcon,
  ConfirmIcon,
  CorrectIcon,
  EditIcon,
  LocationIcon,
  PreviewIcon,
  ResetIcon,
  UserIcon,
} from '../core/icons'
import { Event } from '../core/types'
import { useNotifications } from '../hooks/use-notifications'
import DataGridToolbar from '../components/data-grid-toolbar'

dayjs.extend(utc)
dayjs.extend(isBetween)

let SessionPanel = ({
  event,
  session,
  type,
  attendanceVisible,
  setOpenSession,
}: {
  event?: Event
  session: HydratedSession
  type: 'registered' | 'available' | 'completed'
  attendanceVisible?: boolean
  setOpenSession: React.Dispatch<React.SetStateAction<string>>
}): ReactElement => {
  let dispatch = useAppDispatch()
  let roles = useAppSelector(selectRoles)

  let instructorsList = useAppSelector(selectInstructors)
  let instructorsById = useMemo(
    () => new Map(instructorsList.map((i) => [i.id, i])),
    [instructorsList],
  )
  let instructors = useMemo(
    () =>
      (session.instructorIDs.length
        ? session.instructorIDs
        : event?.instructorIDs || []
      )
        .map((id) => instructorsById.get(id)?.name)
        .filter(Boolean)
        .join(', ') || '',
    [session.instructorIDs, event?.instructorIDs, instructorsById],
  )
  let capacities = useAppSelector(selectSessionCapacity)
  let completions = useAppSelector(selectCompletionEventsByID(session.id))
  let completionsStatus = useAppSelector(
    selectCompletionEventsStatusByID(session.id),
  )
  let users = useAppSelector(selectUsersByEvent(session.eventID))

  let [viewAttendance, setViewAttendance] = useState<boolean>(false)
  let [loading, setLoading] = useState<boolean>(false)
  let [currentTime, setCurrentTime] = useState(dayjs())

  let usersById = useMemo(() => new Map(users?.map((u) => [u.id, u])), [users])
  let completedUserIDs = useMemo(
    () => new Set(completions?.map((c) => c.userID)),
    [completions],
  )
  let statuses = useMemo(
    () =>
      session.invites.map((invite) => {
        let user = usersById.get(invite.userID)
        return {
          id: invite.id,
          name: `${user?.firstName} ${user?.lastName}`,
          attended: completedUserIDs.has(invite.userID),
        }
      }),
    [session.invites, usersById, completedUserIDs],
  )
  let isManager = roles.includes('manager')

  let { NotificationComponent, showError } = useNotifications()

  useEffect(() => {
    let interval = setInterval(() => {
      setCurrentTime(dayjs())
    }, 30000) // Update every 30 seconds

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    if (
      viewAttendance &&
      (!completionsStatus || completionsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEventsBySession(session.id))
    }
  }, [completionsStatus, viewAttendance])

  return (
    <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
      {type !== 'completed' && (
        <div style={{ position: 'relative' }}>
          {type === 'registered' ? (
            <Chip
              variant="outlined"
              label="Registered"
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                borderRadius: 1,
              }}
            />
          ) : (
            <Button
              sx={{ position: 'absolute', top: 2, right: 2 }}
              variant="contained"
              loading={loading}
              disabled={
                capacities[session.id] ? !capacities[session.id].canJoin : false
              }
              onClick={() => {
                setLoading(true)
                axios
                  .post('/api/event/invite-no-email', { sessionID: session.id })
                  .then(() => dispatch(fetchSessions()))
                  .then(() => dispatch(fetchUsersByEvent(event?.id || '')))
                  .catch((err) => showError(pluckError(err)))
                  .finally(() => setLoading(false))
              }}
            >
              RSVP
            </Button>
          )}
        </div>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography
          color={type === 'registered' ? 'primary' : 'textPrimary'}
          sx={{ mb: 1 }}
        >
          {session?.name || 'Session'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <CalendarIcon sx={{ height: 18, width: 18 }} />
          <Typography color="textSecondary">
            {dayjs.utc(session.sessionTime).tz(session.timeZone).format('LL')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <ClockIcon sx={{ height: 18, width: 18 }} />
          <Typography color="textSecondary">
            {`${dayjs
              .utc(session.sessionTime)
              .tz(session.timeZone)
              .format('h:mm A')} - ${(session.endTime
              ? dayjs.utc(session.endTime).tz(session.timeZone)
              : dayjs
                  .utc(session.sessionTime)
                  .tz(session.timeZone)
                  .add(event?.hours || 1, 'hours')
            ).format('h:mm A (z)')}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <LocationIcon sx={{ height: 18, width: 18 }} />
          <Box>
            <Typography color="textSecondary">
              {event?.location.kind !== 'inPerson' && 'Virtual event access'}
            </Typography>
            <Typography color="textSecondary">
              {event?.location.kind !== 'virtual' &&
                `${event?.location.address}, ${event?.location.city}, ${event?.location.state?.slice(0, 1).toUpperCase()}${event?.location.state?.slice(1)} ${event?.location.zip}`}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          <UserIcon sx={{ height: 18, width: 18 }} />
          <Typography color="textSecondary">{instructors}</Typography>
        </Box>
      </Box>
      {(type === 'registered' ||
        (type === 'available' &&
          !event?.linkedServiceID &&
          !event?.featured)) && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          {event?.location.kind !== 'inPerson' &&
            event?.location.url &&
            currentTime.isBetween(
              dayjs.utc(session.sessionTime).subtract(10, 'minutes'),
              dayjs.utc(session.sessionTime).add(event?.hours || 2, 'hours'),
            ) && (
              <Button
                variant="outlined"
                startIcon={<VideoIcon />}
                href={event.location.url}
                target="_blank"
                onClick={() => {
                  axios
                    .post('/api/event/invite-no-email', {
                      sessionID: session.id,
                    })
                    .then(() => dispatch(fetchSessions()))
                }}
              >
                Join Virtual Session
              </Button>
            )}
          {dayjs.utc(session.sessionTime).isAfter(currentTime) && (
            <Box sx={{ m: '-1px' }}>
              <AddToCalendarButton
                name={event?.name}
                options={['Apple', 'Google']}
                location={event?.location.name}
                startDate={dayjs
                  .utc(session.sessionTime)
                  .tz(session.timeZone)
                  .format('YYYY-MM-DD')}
                endDate={dayjs
                  .utc(session.sessionTime)
                  .tz(session.timeZone)
                  .format('YYYY-MM-DD')}
                startTime={dayjs
                  .utc(session.sessionTime)
                  .tz(session.timeZone)
                  .format('HH:mm')}
                endTime={dayjs
                  .utc(session.sessionTime)
                  .tz(session.timeZone)
                  .add(event?.hours || 1, 'hours')
                  .format('HH:mm')}
                timeZone={session.timeZone}
                listStyle="dropup-static"
                styleLight={`
              --btn-background: ${theme.palette.primary.main};
              --btn-hover-background: ${theme.palette.primary.dark};
              --btn-text: ${theme.palette.primary.contrastText};
              --list-background: ${theme.palette.primary.main};
              --list-hover-background: ${theme.palette.primary.light};
              --list-text: ${theme.palette.primary.contrastText};
              --list-hover-text: ${theme.palette.primary.contrastText};
              --btn-border: none;
              --btn-padding-x: 16px;
              --btn-padding-y: 8px;
              --btn-font-weight: 400;
            `}
              ></AddToCalendarButton>
            </Box>
          )}
          {dayjs.utc(session.sessionTime).isBefore(currentTime) &&
            attendanceVisible && (
              <Button
                variant="outlined"
                startIcon={<LocationIcon />}
                onClick={() => setOpenSession(session.id)}
              >
                Record Attendance
              </Button>
            )}
        </Box>
      )}
      {type === 'completed' && attendanceVisible && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            sx={{ mt: 1 }}
            variant="outlined"
            startIcon={<LocationIcon />}
            onClick={() => setOpenSession(session.id)}
          >
            Record Attendance
          </Button>
          {isManager && (
            <Button
              sx={{ mt: 1 }}
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setViewAttendance(!viewAttendance)}
            >
              View Attendance
            </Button>
          )}
        </Box>
      )}
      {viewAttendance && (
        <Card variant="outlined" sx={{ mt: 2, p: 2 }}>
          {completionsStatus !== 'fulfilled' ? (
            <>
              <Skeleton variant="rounded" height={30} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={100} />
            </>
          ) : (
            <>
              <Typography>User Attendance Summary</Typography>
              <Box
                sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'flex-end' }}
              >
                <Chip
                  variant="outlined"
                  color="primary"
                  label={`${statuses?.length || 0} Users Registered`}
                />
                <Chip
                  variant="outlined"
                  color="secondary"
                  label={`${statuses?.filter((s) => s.attended)?.length} Users Attended`}
                />
                <Box sx={{ flexGrow: 1 }}></Box>
                <IconButton onClick={() => setViewAttendance(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <DataGrid
                columns={[
                  { field: 'name', headerName: 'Name', flex: 1 },
                  {
                    field: 'attended',
                    headerName: 'Status',
                    width: 160,
                    renderCell: (params) => (
                      <Chip
                        label={params.value ? 'Attended' : 'Did Not Attend'}
                        color={params.value ? 'success' : 'error'}
                        icon={params.value ? <ConfirmIcon /> : <CloseIcon />}
                      />
                    ),
                  },
                ]}
                rows={statuses}
                slots={{
                  toolbar: () => <DataGridToolbar fileName="user-attendance" />,
                }}
                showToolbar
              />
            </>
          )}
        </Card>
      )}
      {NotificationComponent}
    </Card>
  )
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()

  let useEvents = useAppSelector(selectUseEvents)
  let roles = useAppSelector(selectRoles)
  let userID = useAppSelector(selectUserID)

  let eventID = eventId || ''
  let event = useAppSelector(selectEventById(eventID))
  let eventsStatus = useAppSelector(selectEventsStatus)
  let accreditations = useAppSelector(
    selectAccreditationsByTypeByEvent(eventID),
  )
  let accreditationsStatus = useAppSelector(selectAccreditationsStatus)
  let eventAccreditationsStatus = useAppSelector(
    selectEventAccreditationsStatus,
  )
  let sessions = useAppSelector(selectSessionsByEventID(eventID))
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let users = useAppSelector(selectUsersByEvent(eventID))
  let usersStatus = useAppSelector(selectUsersStatusByEvent(eventID))
  let completionEvents = useAppSelector(selectCompletionEventsByID(eventID))
  let completionEventsStatus = useAppSelector(
    selectCompletionEventsStatusByID(eventID),
  )
  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let sessionResults = useAppSelector(selectQuizSessionResults(eventID))
  let sessionResultsStatus = useAppSelector(
    selectQuizSessionResultsStatus(eventID),
  )
  let ceRecords = useAppSelector(selectEventCERecords(eventID))
  let ceRecordsStatus = useAppSelector(selectEventCERecordsStatus(eventID))
  let instructorsStatus = useAppSelector(selectInstructorsStatus)
  let syllabus = useAppSelector(selectSyllabusByEventID(eventID))
  let syllabusStatus = useAppSelector(selectSyllabusStatusByEventID(eventID))

  let [openSession, setOpenSession] = useState<string>('')
  let [referencesModalOpen, setReferencesModalOpen] = useState(false)
  let [passcode, setPasscode] = useState<string>('')
  let [passcodeError, setPasscodeError] = useState<boolean>(false)
  let [attendees, setAttendees] = useState<string[]>([])
  let [resetModal, setResetModal] = useState<boolean>(false)
  let [loading, setLoading] = useState<boolean>(false)
  let [ceRecordID, setCERecordID] = useState<string>()
  let [downloading, setDownloading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [bannerError, setBannerError] = useState<boolean>(false)

  let sessionCompletionEvents = useAppSelector(
    selectCompletionEventsByID(openSession),
  )
  let sessionCompletionEventsStatus = useAppSelector(
    selectCompletionEventsStatusByID(openSession),
  )

  let isManager = roles.includes('manager')
  let tracking =
    event?.attendanceTrackingMechanism.kind === 'managerTracked' && isManager

  let allRegisteredSessions = sessions.filter((s) =>
    s.invites.some((inv) => inv.userID === userID),
  )
  let registeredSessions = allRegisteredSessions.filter((s) =>
    dayjs.utc(s.endTime || s.sessionTime).isAfter(dayjs()),
  )
  let availableSessions = sessions.filter(
    (s) =>
      !s.invites.some((inv) => inv.userID === userID) &&
      dayjs.utc(s.endTime || s.sessionTime).isAfter(dayjs()),
  )
  let completedSessions = sessions.filter(
    (s) =>
      (tracking && dayjs.utc(s.sessionTime).isBefore(dayjs())) ||
      (s.invites.some((inv) => inv.userID === userID) &&
        dayjs.utc(s.endTime || s.sessionTime).isBefore(dayjs())),
  )

  let filteredCompletionEvents =
    completionEvents?.filter((ce) => ce.eventID === eventID) || []
  let eventCompleted =
    filteredCompletionEvents.length &&
    filteredCompletionEvents[0]?.status !== 'restarted'

  let attendanceVisible =
    (event?.attendanceTrackingMechanism.kind === 'quizPasscode' &&
      !eventCompleted) ||
    tracking

  let openSessionData = sessions.find((s) => s.id === openSession)
  let openSessionInviteIDs = useMemo(
    () => new Set(openSessionData?.invites.map((inv) => inv.userID)),
    [openSessionData],
  )
  let sessionCompletionUserIDs = useMemo(
    () => new Set(sessionCompletionEvents?.map((ce) => ce.userID)),
    [sessionCompletionEvents],
  )
  let sessionUsers = useMemo(
    () =>
      // eslint-disable-next-line fp/no-mutating-methods
      users
        ?.filter(
          (u) =>
            openSessionInviteIDs.has(u.id) &&
            !sessionCompletionUserIDs.has(u.id),
        )
        .sort(
          (a, b) =>
            a.firstName.localeCompare(b.firstName) ||
            a.lastName.localeCompare(b.lastName),
        ) || [],
    [users, openSessionInviteIDs, sessionCompletionUserIDs],
  )

  // eslint-disable-next-line fp/no-mutating-methods
  let accreditationNames = [
    ...Object.entries(accreditations).reduce(
      (all, [name, data]) =>
        data.length && name !== 'ceBroker'
          ? [...all, accreditationDict[name as AccreditationType]]
          : all,
      [] as string[],
    ),
    ...accreditations.ceBroker.map(
      (a) => `CE Broker: ${a.state[0].toUpperCase()}${a.state.slice(1)}`,
    ),
  ].sort()

  let quizCompleted =
    !!quiz &&
    completionEvents?.length > 0 &&
    completionEvents?.[0].status !== 'restarted' &&
    sessionResults?.passed

  let trackingText =
    event?.attendanceTrackingMechanism.kind === 'quizPasscode'
      ? "you enter your session's event passcode."
      : 'your manager confirms your attendance.'

  let capceCode = accreditations.capce[0]?.courseCode
  let anccExpiration = accreditations.ancc[0]?.expirationDate

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (accreditationsStatus === 'uninitialized') {
      dispatch(fetchAccreditations())
    }
  }, [accreditationsStatus])

  useEffect(() => {
    if (eventAccreditationsStatus === 'uninitialized') {
      dispatch(fetchEventAccreditations())
    }
  }, [eventAccreditationsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  useEffect(() => {
    if (eventID && (!usersStatus || usersStatus === 'uninitialized')) {
      dispatch(fetchUsersByEvent(eventID))
    }
  }, [usersStatus, eventID])

  useEffect(() => {
    if (
      eventId &&
      (!completionEventsStatus || completionEventsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEvents(eventID))
    }
  }, [completionEventsStatus, eventID])

  useEffect(() => {
    if (
      isManager &&
      openSession &&
      (!sessionCompletionEventsStatus ||
        sessionCompletionEventsStatus === 'uninitialized')
    ) {
      dispatch(fetchEventCompletionEventsBySession(openSession))
    }
  }, [sessionCompletionEventsStatus, openSession, roles])

  useEffect(() => {
    if (
      eventID &&
      (!sessionResultsStatus || sessionResultsStatus === 'uninitialized')
    ) {
      dispatch(fetchQuizSessionResult(eventID))
    }
  }, [sessionResultsStatus, eventID])

  useEffect(() => {
    if (eventID && (!quizStatus || quizStatus === 'uninitialized')) {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  useEffect(() => {
    if (eventID && (!ceRecordsStatus || ceRecordsStatus === 'uninitialized')) {
      dispatch(fetchEventCERecords(eventID))
    } else if (ceRecordsStatus === 'fulfilled') {
      setCERecordID(ceRecords[0]?.ceRecordID)
    }
  }, [ceRecordsStatus, eventID])

  useEffect(() => {
    if (instructorsStatus === 'uninitialized') {
      dispatch(fetchInstructors())
    }
  }, [instructorsStatus])

  useEffect(() => {
    if (eventID && (!syllabusStatus || syllabusStatus === 'uninitialized')) {
      dispatch(fetchSyllabus(eventID))
    }
  }, [syllabusStatus, eventID])

  return useEvents ? (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 3,
        }}
      >
        <IconButton onClick={() => navigate('/events')}>
          <BackArrowIcon />
        </IconButton>
        <Typography variant="h5">{event?.name}</Typography>
        {((!quiz && eventCompleted) || quizCompleted) && (
          <>
            <Chip
              sx={{ ml: 1 }}
              color="success"
              label="Completed"
              icon={<CorrectIcon />}
            />
            {!event?.linkedServiceID && (
              <Button
                variant="outlined"
                startIcon={<ResetIcon />}
                onClick={() => setResetModal(true)}
              >
                Restart Event Progress
              </Button>
            )}
            <Dialog onClose={() => setResetModal(false)} open={resetModal}>
              <Paper className="px-3 py-1" sx={{ maxWidth: 300 }}>
                <DialogTitle sx={{ textAlign: 'center' }}>
                  Confirm Event Restart
                </DialogTitle>
                <DialogContent className="px-2">
                  Restarting an event allows you to attend this event again and
                  get credit for your current certification schedule.
                  <Box sx={{ height: 8 }}></Box>
                  Upon completion, a new certificate will be generated. Are you
                  sure you want to restart this event?
                </DialogContent>
                <DialogActions className="flex justify-center pt-0 pb-3">
                  <Button
                    onClick={() => {
                      setLoading(true)
                      axios
                        .post('/api/event/restart-event', { eventID })
                        .then(() =>
                          dispatch(fetchEventCompletionEvents(eventID)),
                        )
                        .catch((err) => setErrorMessage(pluckError(err)))
                        .finally(() => {
                          setResetModal(false)
                          setLoading(false)
                        })
                    }}
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    loading={loading}
                    startIcon={<ConfirmIcon />}
                  >
                    Confirm
                  </Button>
                  <Button
                    onClick={() => setResetModal(false)}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CloseIcon />}
                  >
                    Cancel
                  </Button>
                </DialogActions>
              </Paper>
            </Dialog>
          </>
        )}
        <Box sx={{ flexGrow: 1 }}></Box>
        {roles.includes(event?.linkedServiceID ? 'manager' : 'admin') && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/events/${eventID}/edit`)}
          >
            Edit Event
          </Button>
        )}
      </Box>
      <Box
        sx={{
          width: '100%',
          aspectRatio: '4 / 1',
          borderRadius: '10px',
          overflow: 'hidden',
          bgcolor: 'background.paperDark',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        role="img"
        aria-label={
          bannerError || !event?.banner
            ? `${event?.name || 'Event'} banner placeholder`
            : undefined
        }
      >
        {!bannerError && event?.banner && (
          <img
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
            src={`${import.meta.env.VITE_CLOUDFRONT_URL}/images/${event.banner.replace('originals', 'resized')}`}
            alt={`${event.name} banner`}
            onError={() => setBannerError(true)}
          />
        )}
      </Box>
      <Box
        sx={{
          mt: 4,
          display: 'grid',
          gridTemplateColumns: {
            sm: '1fr',
            md: '5fr 2fr',
          },
          gap: 2.5,
        }}
      >
        <Box>
          <Typography variant="h6">About Event</Typography>
          <Card
            sx={{
              p: 2,
              mt: 1,
              bgcolor: 'background.paperDark',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            <Typography>{syllabus?.text || event?.description}</Typography>

            {(syllabus?.objectives?.length ?? 0) > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2 }}>
                <Typography variant="subtitle1" sx={{ mt: 2 }}>
                  Objectives
                </Typography>
                {syllabus?.objectives?.map((objective, index) => (
                  <Typography
                    key={objective.id}
                    sx={{ mb: 0.5, fontSize: '0.8rem' }}
                  >
                    {index + 1}. {objective.text}
                  </Typography>
                ))}
              </Box>
            )}
          </Card>
        </Box>
        <Box sx={{ height: { sm: 'fit-content', md: 0 } }}>
          {event?.categoryName && event?.hours > 0 && (
            <>
              <Typography variant="h6">Hour Type / CE Hours</Typography>
              <Card
                sx={{ p: 2, mt: 1, mb: 2, bgcolor: 'background.paperDark' }}
              >
                <Typography>
                  {event.categoryName.replace(/\|/g, '/')} ({event.hours} hour
                  {event.hours === 1 ? '' : 's'}
                  {!event.linkedServiceID ? ' F5' : ''}
                  {!accreditationNames.length ||
                  accreditationNames.includes('Not Accredited')
                    ? ''
                    : ' CE'}
                  )
                </Typography>
              </Card>
            </>
          )}
          <Typography variant="h6">Accreditations</Typography>
          <Card sx={{ p: 2, mt: 1, bgcolor: 'background.paperDark' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {!accreditationNames.length ? (
                <Typography color="textSecondary">
                  This event has no accreditations.
                </Typography>
              ) : (
                accreditationNames.map((a) => (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <VerifiedIcon color="primary" />
                    <Typography>{a}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Card>
          <Typography sx={{ mt: 2 }} variant="h6">
            {!quiz || quizCompleted ? 'Event Certificate' : 'Event Quiz'}
          </Typography>
          <Card sx={{ p: 2, mt: 1, bgcolor: 'background.paperDark' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {!quiz || quizCompleted ? (
                !event?.hours ? (
                  <Typography color="textSecondary">
                    {!eventCompleted
                      ? `You will get credit for completing this event once ${trackingText}`
                      : 'This event does not have a certificate, but you have been given credit for its completion.'}
                  </Typography>
                ) : (
                  <>
                    {!ceRecordID ? (
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() =>
                          navigate(`/events/${event?.id}/certificate`)
                        }
                        disabled={!eventCompleted}
                      >
                        Get Certificate
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        loading={downloading}
                        disabled={!eventCompleted}
                        onClick={() => {
                          setDownloading(true)
                          new Promise((resolve) => {
                            setTimeout(resolve, 5000)
                          }).then(() =>
                            axios
                              .get(
                                `/api/event/certificate?ceRecordID=${ceRecordID}`,
                              )
                              .then((response) => {
                                axios
                                  .get(response.data, { responseType: 'blob' })
                                  .then((resp) => {
                                    let url = window.URL.createObjectURL(
                                      new Blob([resp.data]),
                                    )
                                    let link = document.createElement('a')
                                    // eslint-disable-next-line fp/no-mutation
                                    link.href = url
                                    link.setAttribute(
                                      'download',
                                      `${event?.name}.pdf`,
                                    )
                                    document.body.appendChild(link)
                                    link.click()
                                  })
                                  .catch((err) => {
                                    setErrorMessage(
                                      pluckError(
                                        err,
                                        'Error downloading certificate',
                                      ),
                                    )
                                  })
                                  .finally(() => setDownloading(false))
                              }),
                          )
                        }}
                      >
                        Download Certificate
                      </Button>
                    )}
                    {!eventCompleted && (
                      <Typography color="textSecondary">
                        You cannot access your certificate until {trackingText}
                      </Typography>
                    )}
                  </>
                )
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => navigate(`/events/${event?.id}/quiz`)}
                    disabled={!eventCompleted}
                  >
                    Start Quiz
                  </Button>
                  {!eventCompleted && (
                    <Typography color="textSecondary">
                      The quiz cannot be taken until {trackingText}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Card>
          {(syllabus?.references?.length ?? 0) > 0 && (
            <>
              <Typography sx={{ mt: 2 }} variant="h6">
                References
              </Typography>
              <Card sx={{ p: 2, mt: 1, bgcolor: 'background.paperDark' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setReferencesModalOpen(true)}
                  >
                    View References
                  </Button>
                </Box>
              </Card>
            </>
          )}
        </Box>
        <Box>
          <Typography variant="h6">Registered Sessions</Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!registeredSessions.length ? (
              <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                You have not registered for any event sessions.
              </Card>
            ) : (
              registeredSessions.map((s) => (
                <SessionPanel
                  key={s.id}
                  event={event}
                  session={s}
                  type="registered"
                  attendanceVisible={attendanceVisible}
                  setOpenSession={setOpenSession}
                />
              ))
            )}
          </Box>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Available Sessions
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!availableSessions.length ? (
              <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                There are no available event sessions at this time.
              </Card>
            ) : (
              availableSessions.map((s) => (
                <SessionPanel
                  key={s.id}
                  event={event}
                  session={s}
                  type="available"
                  attendanceVisible={attendanceVisible}
                  setOpenSession={setOpenSession}
                />
              ))
            )}
          </Box>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Completed Sessions
          </Typography>
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {!completedSessions.length ? (
              <Card sx={{ p: 2, bgcolor: 'background.paperDark' }}>
                There have been no completed event sessions at this time.
              </Card>
            ) : (
              completedSessions.map((s) => (
                <SessionPanel
                  key={s.id}
                  event={event}
                  session={s}
                  type="completed"
                  attendanceVisible={attendanceVisible}
                  setOpenSession={setOpenSession}
                />
              ))
            )}
          </Box>
          {!!event &&
            !event?.linkedServiceID &&
            (capceCode || anccExpiration || quiz) && (
              <>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  Information
                </Typography>
                <Card
                  sx={{
                    p: 2,
                    mt: 1,
                    bgcolor: 'background.paperDark',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  {(capceCode || anccExpiration) && (
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Course Accreditations:
                      </Typography>
                      <Box sx={{ pl: 1 }}>
                        {capceCode && (
                          <Typography>
                            {'\u2022 '} This CE activity is accredited by the
                            Commission of Accreditation for Prehospital
                            Continuing Education for ({event?.hours}){' '}
                            Based/Advanced CEH.
                          </Typography>
                        )}
                        {anccExpiration && (
                          <Typography>
                            {'\u2022 '} FOAMfrat is accredited as a provider of
                            nursing continuing professional development by the
                            American Nurses Credentialing Center's Commission on
                            Accreditation.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      Criteria for Receiving Contact Hours:
                    </Typography>
                    <Box sx={{ pl: 1 }}>
                      <Typography>
                        {'\u2022 '} Attendance for 100% of the activity
                      </Typography>
                      {!!quiz && (
                        <Typography>
                          {'\u2022 '} Successful completion of a post-course
                          quiz
                        </Typography>
                      )}
                      <Typography>
                        {'\u2022 '} Completion and submission of post-course
                        evaluation form
                      </Typography>
                    </Box>
                  </Box>
                  {anccExpiration && (
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        ANCC Expiration Date:
                      </Typography>
                      <Typography>
                        {dayjs(anccExpiration).format('MMMM DD, YYYY')}
                      </Typography>
                    </Box>
                  )}
                  {capceCode && (
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Relationship Disclosure:
                      </Typography>
                      <Typography>
                        None of the planners or presenters for this activity
                        have relevant financial relationship(s) to disclose with
                        ineligible companies.
                      </Typography>
                    </Box>
                  )}
                  {capceCode && (
                    <Box>
                      <Typography
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        Contact Information:
                      </Typography>
                      <Typography>
                        For inquiries or support, please contact FOAMfrat LLC at
                        support@foamfrat.com or visit our website at
                        www.foamfrat.com.
                      </Typography>
                    </Box>
                  )}
                </Card>
              </>
            )}
        </Box>
      </Box>
      <Loading
        entities={[
          accreditationsStatus,
          eventAccreditationsStatus,
          eventsStatus,
          sessionsStatus,
          sessionResultsStatus,
          completionEventsStatus,
          quizStatus,
        ]}
      />
      <Dialog open={!!openSession} onClose={() => setOpenSession('')}>
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 640 }}>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 1,
            }}
          >
            Confirm Attendance
          </Typography>
          {event?.attendanceTrackingMechanism.kind === 'quizPasscode' ? (
            <TextField
              fullWidth
              variant="outlined"
              label="Event Passcode"
              error={passcodeError}
              helperText={
                passcodeError ? 'Incorrect event passcode' : undefined
              }
              value={passcode}
              onChange={(e) => {
                setPasscodeError(false)
                setPasscode(e.target.value)
              }}
            />
          ) : sessionUsers.length ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label="Select All"
                clickable
                onClick={() => setAttendees(sessionUsers.map((su) => su.id))}
              />
              {sessionUsers.map((u) => (
                <Chip
                  label={`${u.firstName} ${u.lastName}`}
                  icon={
                    attendees.includes(u.id) ? <ConfirmIcon /> : <CloseIcon />
                  }
                  color={attendees.includes(u.id) ? 'success' : 'default'}
                  clickable
                  onClick={() =>
                    setAttendees(
                      attendees.includes(u.id)
                        ? attendees.filter((cu) => cu !== u.id)
                        : [...attendees, u.id],
                    )
                  }
                />
              ))}
            </Box>
          ) : (
            <Typography>Attendance has been taken for all users.</Typography>
          )}
          <Box
            sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}
          >
            {!sessionUsers.length &&
            event?.attendanceTrackingMechanism.kind === 'managerTracked' ? (
              <Button
                variant="contained"
                onClick={() => {
                  setAttendees([])
                  setOpenSession('')
                }}
              >
                OK
              </Button>
            ) : (
              <>
                <Button
                  variant="contained"
                  loading={loading}
                  onClick={() => {
                    if (
                      event?.attendanceTrackingMechanism.kind ===
                        'quizPasscode' &&
                      event?.attendanceTrackingMechanism.quizPasscode?.toLowerCase() !==
                        passcode.toLowerCase()
                    ) {
                      setPasscodeError(true)
                      return
                    }
                    setLoading(true)
                    let request =
                      event?.attendanceTrackingMechanism.kind === 'quizPasscode'
                        ? axios.post('/api/event/completion-event', {
                            sessionID: openSession,
                          })
                        : axios
                            .post('/api/event/completion-events', {
                              userIDs: attendees,
                              sessionID: openSession,
                            })
                            .then((): Promise<any> => {
                              if (event?.hours && !quiz) {
                                let limit = pLimit(6)

                                let uploadPromises = attendees.map((attendee) =>
                                  limit(() =>
                                    axios.post('/api/event/ce-record', {
                                      eventID,
                                      userID: attendee,
                                    }),
                                  ),
                                )

                                return Promise.all(uploadPromises)
                              }
                              return Promise.resolve()
                            })
                    request
                      .then(() => {
                        dispatch(fetchEventCompletionEvents(eventID))
                        dispatch(
                          fetchEventCompletionEventsBySession(openSession),
                        )
                        dispatch(fetchEventCERecords(eventID))
                        dispatch(fetchQuizSessionResult(eventID))
                      })
                      .catch((err) =>
                        setErrorMessage(
                          pluckError(err, 'Error tracking attendance'),
                        ),
                      )
                      .finally(() => {
                        setLoading(false)
                        setAttendees([])
                        setOpenSession('')
                      })
                  }}
                >
                  Confirm
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setAttendees([])
                    setOpenSession('')
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Dialog>
      <Dialog
        open={referencesModalOpen}
        onClose={() => setReferencesModalOpen(false)}
      >
        <Box sx={{ p: 3, minWidth: 400, maxWidth: 600 }}>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 2,
            }}
          >
            References
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {syllabus?.references?.map((reference) => (
              <Typography key={reference.id}>
                {'\u2022 '} {reference.text}
              </Typography>
            ))}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={() => setReferencesModalOpen(false)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  )
}
